/**
 * Fuente única de los EntityType del motor de aprobaciones en el frontend.
 *
 * Estaba duplicada entre la bandeja (pending-approvals-page) y la pantalla de revisión
 * (approval-review-page): agregar un módulo nuevo al motor obligaba a acordarse de tocar las dos,
 * y la que quedaba desactualizada mostraba el EntityType crudo en vez del nombre amigable.
 *
 * Los valores tienen que coincidir con el `ApprovalFlow.EntityType` del backend
 * (ver `Utils/PurchaseOrderApproval.EntityType` y `Utils/VacationConstants`).
 */

/** Nombres amigables por EntityType. Se agregan entradas a medida que otros módulos adopten el motor. */
export const ENTITY_TYPE_LABELS: Record<string, string> = {
    Nomina: 'Nómina',
    PayrollPeriod: 'Nómina',
    PurchaseOrder: 'Orden de Compra',
    // Los tres del módulo de vacaciones. Sin estas entradas el título de la pantalla de revisión
    // mostraba el EntityType crudo ("Revisión de VacationRequest") al aprobador que llegaba del correo.
    VacationRequest: 'Solicitud de Vacaciones',
    VacationAdjustment: 'Ajuste de Saldo de Vacaciones',
    VacationSeniorityPayout: 'Pago de Días por Antigüedad',
};

/** EntityTypes que resuelven contra una subnómina de Talento Humano. */
export const PAYROLL_ENTITY_TYPES = new Set(['Nomina', 'PayrollPeriod', 'PayrollSubnomina']);

/** EntityType del flujo de aprobación por monto de órdenes de compra. */
export const PURCHASE_ORDER_ENTITY_TYPES = new Set(['PurchaseOrder']);

/**
 * EntityTypes que resuelven contra una solicitud de vacaciones.
 *
 * Los tres comparten pantalla de detalle porque la pregunta del aprobador es la misma —quién pide,
 * cuánto, contra qué saldo—; lo que cambia son dos o tres campos. Tienen que coincidir con
 * `Utils/VacationConstants` del backend.
 */
export const VACATION_ENTITY_TYPES = new Set([
    'VacationRequest',
    'VacationAdjustment',
    'VacationSeniorityPayout',
]);

export function isPayrollEntity(entityType: string): boolean {
    return PAYROLL_ENTITY_TYPES.has(entityType);
}

export function isPurchaseOrderEntity(entityType: string): boolean {
    return PURCHASE_ORDER_ENTITY_TYPES.has(entityType);
}

export function isVacationEntity(entityType: string): boolean {
    return VACATION_ENTITY_TYPES.has(entityType);
}

/**
 * Rótulos de los códigos de Concreto. Fuente única para TODA pantalla que muestre una orden de
 * compra o una requisición, para que Sym70 y Concreto nombren igual al mismo dato.
 *
 * Salen de la pantalla "Seguimiento OC" de Concreto (`OrdenCompra/seguimiento-oc.html`), que es la
 * que tiene a la vista el Jefe de Compras. Existe porque las pantallas los escribían a mano y ya
 * había drift: el listado de requisiciones rotulaba "# de Orden" una columna que muestra el id de
 * la REQUISICIÓN, y el detalle de la orden rotulaba "ID de Orden" el PK interno de Sym70.
 */
export const CONCRETO_CODE_LABELS = {
    /** `ordenes_header.numero_orden`, ej. "8842-1". El código con el que se habla de una OC. */
    orderNumber: 'N° Orden',
    /** `requisicion_header.codPedido`, ej. "EQU00001". El código con el que se habla de un pedido. */
    requisitionCode: 'Cod. Pedido',
    /** `requisicion_header.id`. Fallback cuando la requisición no tiene Cod. Pedido. */
    requisitionId: 'ID Req.',
    /** `ordenes_header.id`. No es un código de negocio: sirve para soporte y para enlazar a Concreto. */
    concretoOrderId: 'ID Concreto',
    /** PK de la tabla Order en Sym70. Solo existe en esta base — nunca presentarlo como "la orden". */
    sym70OrderId: 'ID Sym70'
} as const;

/** Texto de un valor ausente. Un guion, nunca un id interno de relleno. */
export const CODE_PLACEHOLDER = '—';

/** Nombre de la entidad de una fila del motor: "Orden de Compra". */
export function approvalEntityLabel(entityType: string): string {
    return ENTITY_TYPE_LABELS[entityType] ?? entityType;
}

/** Cómo se nombra un documento concreto en un título: "Orden de Compra 8842-1". */
export function approvalEntityTitle(entityType: string, targetEntityId: number, code?: string | null): string {
    const name = approvalEntityLabel(entityType);
    // Sin código se cae al id de Sym70: no significa nada fuera de esta base, pero deja el título
    // diagnosticable en vez de mudo.
    return code?.trim() ? `${name} ${code}` : `${name} — #${targetEntityId}`;
}
